<template>
    <pendo-popover
        class="theme-popover"
        trigger="manual"
        placement="right-start"
        container="body"
        :popover-class="['theme-popover__popover']"
        :open="isOpen"
        @update:open="handlePopoverUpdate">
        <pendo-button
            ref="themePopoverTrigger"
            class="theme-popover__trigger nav-option"
            pendo-analytics="theme-popover"
            aria-haspopup
            type="tertiary"
            size="small"
            :aria-label="$t('themeMenu.Theme')"
            @click="handlePopoverUpdate(!isOpen)"
            @mouseenter="handleMouseenter"
            @mouseleave="handleMouseleave"
            @keydown.space.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.enter.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.tab.prevent="handlePopoverUpdate(!isOpen)"
            @keydown.up.prevent="$emit('pressed-up', $event)"
            @keydown.down.prevent="$emit('pressed-down', $event)">
            <sun-moon-icon class="theme-popover__option-prefix-icon" />
            <div class="theme-popover__option-label">
                {{ $t('themeMenu.Theme') }}
            </div>
            <pendo-icon
                class="theme-popover__option-suffix-icon"
                type="chevron-right"
                size="14" />
        </pendo-button>
        <template #popover>
            <div
                ref="themePopoverContent"
                class="theme-popover__content"
                @mouseenter="handleMouseenter"
                @mouseleave="handleMouseleave"
                @keydown.escape.prevent="handleEscapePopover">
                <pendo-button
                    v-for="(option, index) in options"
                    :key="option.id"
                    class="nav-option theme-popover__option"
                    type="tertiary"
                    :pendo-analytics="option.analytics"
                    :aria-label="option.label"
                    @click.prevent.stop="handleOptionClick(option)"
                    @keydown.space.prevent.stop="handleOptionClick(option)"
                    @keydown.enter.prevent.stop="handleOptionClick(option)"
                    @keydown.tab.prevent.stop="handleOptionClick(option)"
                    @keydown.up.prevent="handleArrowKeyNavigation($event, index)"
                    @keydown.down.prevent="handleArrowKeyNavigation($event, index)">
                    <div
                        class="theme-popover__option-tag"
                        :class="{
                            'theme-popover__option-tag--dark': isDark(option.id),
                        }">
                        <p class="theme-popover__option-tag-text">
                            {{ $t('themeMenu.Aa') }}
                        </p>
                    </div>
                    <div class="theme-popover__option-label">
                        {{ option.label }}
                    </div>
                    <pendo-icon
                        v-if="isSelected(option.id)"
                        class="theme-popover__option-suffix-icon"
                        type="check"
                        size="14" />
                </pendo-button>
            </div>
        </template>
    </pendo-popover>
</template>

<script>
import { ref } from 'vue';
import { PendoButton, PendoIcon, PendoPopover } from '@pendo/components';
import SunMoonIcon from '@/top-nav/components/icons/sun-moon-icon.vue';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';
import { useArrowKeyNavigation } from '@/common/use/use-arrow-key-navigation/use-arrow-key-navigation';

export default {
    name: 'ThemePopover',
    components: {
        PendoButton,
        PendoIcon,
        PendoPopover,
        SunMoonIcon,
    },
    emits: ['pressed-up', 'pressed-down', 'select'],
    setup () {
        const { isSystemDarkMode, darkModeInLocalStorage, setDarkMode } = useDarkMode();

        const themePopoverContent = ref(null);
        const { handleArrowKeyNavigation } = useArrowKeyNavigation({
            containerRef: themePopoverContent,
            selector: '.nav-option',
        });

        return {
            isSystemDarkMode,
            darkModeInLocalStorage,
            themePopoverContent,
            handleArrowKeyNavigation,

            setDarkMode,
        };
    },
    data () {
        return {
            isOpen: false,
            mouseleaveTimeout: null,
        };
    },
    computed: {
        options () {
            return [
                {
                    id: 'system',
                    label: this.$t('themeMenu.System theme'),
                    analytics: 'theme-popover-system-theme',
                    onClick: () => this.setDarkMode('system'),
                },
                {
                    id: 'light',
                    label: this.$t('themeMenu.Light'),
                    analytics: 'theme-popover-light-theme',
                    onClick: () => this.setDarkMode(false),
                },
                {
                    id: 'dark',
                    label: this.$t('themeMenu.Dark'),
                    analytics: 'theme-popover-dark-theme',
                    onClick: () => this.setDarkMode(true),
                },
            ];
        },
    },
    methods: {
        isDark (id) {
            switch (id) {
                case 'system':
                    return this.isSystemDarkMode;
                case 'light':
                    return false;
                case 'dark':
                    return true;
                default:
                    return false;
            }
        },
        isSelected (id) {
            switch (id) {
                case 'system':
                    return this.darkModeInLocalStorage === 'system';
                case 'light':
                    return this.darkModeInLocalStorage === false;
                case 'dark':
                    return this.darkModeInLocalStorage === true;
                default:
                    return false;
            }
        },
        handleMouseenter () {
            this.clearMouseenterTimeout();
            this.clearMouseleaveTimeout();

            if (!this.isOpen) {
                this.isOpen = true;
            }
        },
        handleMouseleave () {
            this.clearMouseenterTimeout();
            this.clearMouseleaveTimeout();

            this.mouseleaveTimeout = setTimeout(() => {
                this.isOpen = false;
            }, 250);
        },
        clearMouseenterTimeout () {
            if (this.mouseenterTimeout) {
                clearTimeout(this.mouseenterTimeout);
                this.mouseenterTimeout = null;
            }
        },
        clearMouseleaveTimeout () {
            if (this.mouseleaveTimeout) {
                clearTimeout(this.mouseleaveTimeout);
                this.mouseleaveTimeout = null;
            }
        },
        handlePopoverUpdate (open) {
            if (open) {
                this.isOpen = true;
                this.focusFirstOption();
            } else {
                this.isOpen = false;
            }
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.themePopoverContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        handleEscapePopover () {
            this.handlePopoverUpdate(false);
            this.$refs.themePopoverTrigger?.$el?.focus();
        },
        handleOptionClick (option) {
            option.onClick();
            this.handlePopoverUpdate(false);
            this.$emit('select');
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.theme-popover {
    padding: 0 !important;
    height: 32px;

    :deep(.trigger) {
        width: 100%;
        height: 100%;
    }

    &__option {
        margin: 0 !important;
        width: 100%;
        padding: 6px 4px !important;
        display: flex !important;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -1px;
        }

        &:hover {
            text-decoration: none;
            background-color: var(--palette-gray-20);
        }

        &-prefix-icon {
            flex-shrink: 0;
            color: var(--palette-gray-90);
        }

        &-suffix-icon {
            margin-left: auto;
            color: var(--palette-gray-60);
        }

        :deep(span) {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            height: 32px;

            svg {
                height: 14px;
                width: 14px;
                padding: 5px;
                box-sizing: content-box;
            }
        }

        &-tag {
            font-weight: 400;
            padding: 1px 5px;
            border-radius: 4px;
            border: 1px solid var(--palette-gray-40);
            color: var(--static-palette-gray-100);
            background-color: var(--static-palette-gray-0);
            display: flex;
            align-items: center;

            &-text {
                margin: 0;
            }

            &--dark {
                color: var(--static-palette-gray-0);
                background-color: var(--static-palette-gray-100);
            }

            &::before {
                content: '';
                display: inline-block;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: var(--palette-teal-70);
                margin-right: 4px;
            }
        }

        &-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__trigger {
        width: 100%;
        transition: none;
        border-radius: 5px;
        padding: 6px 8px !important;
        margin: 0 !important;

        & > :deep(span) {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 4px;
        }

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }

        &:hover {
            text-decoration: none;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 6px;
        width: 200px;
    }
}
</style>

<style lang="less">
.theme-popover__popover {
    z-index: 20001;

    .pendo-popover__inner {
        padding: 0;
    }
}
</style>